import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";
import { scrollToId } from "../lib/scroll";
import { Ornament } from "./Chapter";
import { useLanguage, LANGS } from "../i18n/LanguageContext";
import { antesDoFim1aEdicao } from "../data/datas";

// Menu principal. `label(t)` devolve o texto traduzido; `ate1aEdicao` esconde o item a partir de 15/11/2026.
const LINKS = [
    {
        key: "noiteFados", route: "/experiencia", testid: "nav-link-noite-fados", label: (t) => t.nav.noiteFados,
        children: [
            { key: "menu", route: "/experiencia?tab=menu", label: (t) => t.experiencia.tabs.menu },
            { key: "cultura", route: "/experiencia?tab=cultura", label: (t) => t.experiencia.tabs.cultura },
            { key: "gastro", route: "/experiencia?tab=gastro", label: (t) => t.experiencia.tabs.gastro },
            { key: "bar", route: "/experiencia?tab=bar", label: (t) => t.experiencia.tabs.bar },
            { key: "equipa", route: "/equipa", label: (t) => t.nav.equipa, ate1aEdicao: true },
        ],
    },
    { key: "artistas", href: "#artistas", testid: "nav-link-artistas", label: (t) => t.nav.artistas },
    { key: "noticias", route: "/noticias", testid: "nav-link-noticias", label: (t) => t.nav.noticias },
    {
        key: "associacao", route: "/associacao", testid: "nav-link-associacao", label: (t) => t.nav.associacao,
        children: [
            { key: "missao", route: "/missao", label: (t) => t.nav.missao },
            { key: "vantagens", route: "/socios/vantagens", label: (t) => t.nav.vantagens },
        ],
    },
    { key: "local", route: "/local", testid: "nav-link-local", label: (t) => t.nav.local },
    { key: "contacto", route: "/contacto", testid: "nav-link-contacto", label: (t) => t.nav.contacto },
];

const filhosVisiveis = (l) => (l.children || []).filter((c) => !c.ate1aEdicao || antesDoFim1aEdicao());

const itemCls = "font-caps text-[10px] 2xl:text-[11px] tracking-[0.14em] 2xl:tracking-[0.18em] uppercase whitespace-nowrap text-[#efe6d6]/80 hover:text-[#c9a24b] transition-colors duration-300";

const DesktopItem = ({ l, t, go }) => {
    const filhos = filhosVisiveis(l);
    const trigger = l.route ? (
        <Link to={l.route} data-testid={l.testid} className={`${itemCls} inline-flex items-center gap-1 py-2`}>
            {l.label(t)}
            {filhos.length > 0 && <ChevronDown size={11} className="text-[#c9a24b]/70 group-hover:rotate-180 transition-transform duration-300" aria-hidden="true" />}
        </Link>
    ) : (
        <button onClick={() => go(l.href)} data-testid={l.testid} className={`${itemCls} py-2`}>{l.label(t)}</button>
    );
    if (!filhos.length) return trigger;
    return (
        <div className="relative group" data-testid={`nav-group-${l.key}`}>
            {trigger}
            <div className="absolute left-1/2 -translate-x-1/2 top-full pt-3 invisible opacity-0 translate-y-1 group-hover:visible group-hover:opacity-100 group-hover:translate-y-0 group-focus-within:visible group-focus-within:opacity-100 group-focus-within:translate-y-0 transition-[opacity,transform,visibility] duration-300">
                <div className="relative min-w-[220px] bg-[#070503]/97 backdrop-blur-md border border-[#c9a24b]/25 py-3 shadow-[0_20px_60px_-20px_rgba(0,0,0,0.9)]" data-testid={`nav-submenu-${l.key}`}>
                    <span aria-hidden="true" className="absolute -top-1.5 left-1/2 -translate-x-1/2 w-3 h-3 rotate-45 bg-[#070503] border-l border-t border-[#c9a24b]/25" />
                    {filhos.map((c) => (
                        <Link key={c.key} to={c.route} data-testid={`nav-sub-${c.key}`}
                            className="block px-5 py-2.5 font-caps text-[10px] tracking-[0.18em] uppercase text-[#efe6d6]/75 hover:text-[#c9a24b] hover:bg-[#c9a24b]/5 transition-colors duration-200">
                            {c.label(t)}
                        </Link>
                    ))}
                </div>
            </div>
        </div>
    );
};

export const Navbar = () => {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const { lang, setLang, t } = useLanguage();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 40);
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const go = (href) => {
        setOpen(false);
        scrollToId(href);
    };

    const langClass = (l) =>
        `font-caps text-[11px] xl:text-[10px] 2xl:text-[11px] tracking-[0.2em] xl:tracking-[0.1em] 2xl:tracking-[0.2em] uppercase transition-colors duration-300 ${
            lang === l ? "text-[#c9a24b] border-b border-[#c9a24b] pb-0.5" : "text-[#efe6d6]/70 hover:text-[#efe6d6]"
        }`;

    return (
        <header
            data-testid="main-navbar"
            className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,backdrop-filter,border-color] duration-500 border-b ${
                scrolled ? "bg-[#070503]/85 backdrop-blur-md border-[#c9a24b]/15" : "bg-transparent border-transparent"
            }`}
        >
            <div className="mx-auto max-w-[1500px] px-5 sm:px-10 py-4 lg:py-5 flex items-center justify-between">
                <button onClick={() => go("#topo")} data-testid="nav-logo" aria-label="Fados à Mesa — voltar ao topo" className="shrink-0">
                    <img src="/images/logo.webp" alt="Fados à Mesa — 1ª Edição" className="h-11 sm:h-14 xl:h-11 2xl:h-12 w-auto" width="1295" height="388" />
                </button>

                <nav className="hidden xl:flex items-center gap-5 2xl:gap-7 px-4" data-testid="nav-links">
                    {LINKS.map((l) => <DesktopItem key={l.key} l={l} t={t} go={go} />)}
                </nav>

                <div className="flex items-center gap-4 sm:gap-6 xl:gap-4 2xl:gap-5 shrink-0">
                    <div className="hidden md:flex items-center gap-3 xl:gap-2 2xl:gap-3" data-testid="language-switcher">
                        {LANGS.map((l) => (
                            <button key={l} onClick={() => setLang(l)} aria-pressed={lang === l} data-testid={`lang-${l}`} className={langClass(l)}>
                                {l}
                            </button>
                        ))}
                    </div>
                    <Link
                        to="/bilhetes"
                        data-testid="reservar-button"
                        className="hidden sm:block font-caps text-xs xl:text-[10px] 2xl:text-xs tracking-[0.25em] xl:tracking-[0.15em] 2xl:tracking-[0.22em] uppercase whitespace-nowrap text-[#c9a24b] border border-[#c9a24b]/60 px-6 xl:px-4 2xl:px-5 py-2.5 hover:bg-[#c9a24b] hover:text-[#070503] transition-colors duration-400"
                    >
                        {t.nav.reservar}
                    </Link>
                    <button className="xl:hidden text-[#efe6d6]" onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle" aria-label="Menu">
                        {open ? <X size={24} /> : <Menu size={24} />}
                    </button>
                </div>
            </div>

            {open && (
                <div className="xl:hidden bg-[#070503]/97 backdrop-blur-md border-t border-[#c9a24b]/15 px-6 py-8 flex flex-col gap-5 max-h-[calc(100vh-7rem)] overflow-y-auto" data-testid="mobile-menu">
                    {LINKS.map((l) => {
                        const filhos = filhosVisiveis(l);
                        return (
                            <div key={l.key} className="flex flex-col gap-2.5">
                                {l.route ? (
                                    <Link to={l.route} onClick={() => setOpen(false)} data-testid={`mobile-${l.testid}`}
                                        className="font-caps text-sm tracking-[0.25em] uppercase text-[#efe6d6]/85 text-left">
                                        {l.label(t)}
                                    </Link>
                                ) : (
                                    <button onClick={() => go(l.href)} data-testid={`mobile-${l.testid}`}
                                        className="font-caps text-sm tracking-[0.25em] uppercase text-[#efe6d6]/85 text-left">
                                        {l.label(t)}
                                    </button>
                                )}
                                {filhos.length > 0 && (
                                    <div className="flex flex-col gap-2 pl-4 border-l border-[#c9a24b]/25" data-testid={`mobile-submenu-${l.key}`}>
                                        {filhos.map((c) => (
                                            <Link key={c.key} to={c.route} onClick={() => setOpen(false)} data-testid={`mobile-nav-sub-${c.key}`}
                                                className="font-caps text-[11px] tracking-[0.2em] uppercase text-[#efe6d6]/60 hover:text-[#c9a24b] text-left">
                                                {c.label(t)}
                                            </Link>
                                        ))}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                    <div className="flex items-center gap-5 pt-2" data-testid="mobile-language-switcher">
                        {LANGS.map((l) => (
                            <button key={l} onClick={() => setLang(l)} aria-pressed={lang === l} data-testid={`mobile-lang-${l}`} className={langClass(l)}>
                                {l}
                            </button>
                        ))}
                    </div>
                    <Ornament className="w-32" />
                </div>
            )}
        </header>
    );
};
